/**
 * Fog of War System
 * 
 * Implements recursive shadowcasting for line-of-sight visibility in sanctums.
 * Based on the classic roguelike FOV algorithm.
 */


interface Tile {
  x: number;
  y: number;
  visible: boolean;
  explored: boolean;
}

export class FogOfWarSystem {
  private tiles: Map<string, Tile> = new Map();
  private visibleTiles: Set<string> = new Set();
  private viewRadius: number;

  constructor(viewRadius: number = 12) {
    this.viewRadius = viewRadius;
  }

  /**
   * Calculate visible tiles from player position using shadowcasting
   */
  public calculateVisibility(
    playerTileX: number,
    playerTileY: number,
    isWallCallback: (x: number, y: number) => boolean
  ): Set<string> {
    // Clear previous visibility
    this.visibleTiles.clear();

    // Player position is always visible
    const playerKey = `${playerTileX},${playerTileY}`;
    this.visibleTiles.add(playerKey);
    this.markExplored(playerTileX, playerTileY);

    // Cast shadows in 8 octants
    for (let octant = 0; octant < 8; octant++) {
      this.castLight(
        playerTileX,
        playerTileY,
        1,
        1.0,
        0.0,
        octant,
        isWallCallback
      );
    }

    return this.visibleTiles;
  }

  /**
   * Recursive shadowcasting algorithm
   */
  private castLight(
    cx: number,
    cy: number,
    row: number,
    startSlope: number,
    endSlope: number,
    octant: number,
    isWallCallback: (x: number, y: number) => boolean
  ): void {
    if (startSlope < endSlope) return;

    let nextStartSlope = startSlope;

    for (let i = row; i <= this.viewRadius; i++) {
      let blocked = false;

      for (let dx = -i; dx <= 0; dx++) {
        const dy = -i;
        const leftSlope = (dx - 0.5) / (dy + 0.5);
        const rightSlope = (dx + 0.5) / (dy - 0.5);

        if (startSlope < rightSlope) {
          continue;
        } else if (endSlope > leftSlope) {
          break;
        }

        // Transform based on octant
        const [mapX, mapY] = this.transformOctant(cx, cy, dx, dy, octant);
        
        // Check if in radius
        const dist = Math.sqrt((mapX - cx) ** 2 + (mapY - cy) ** 2);
        if (dist <= this.viewRadius) {
          const tileKey = `${mapX},${mapY}`;
          this.visibleTiles.add(tileKey);
          this.markExplored(mapX, mapY);
        }

        if (blocked) {
          if (isWallCallback(mapX, mapY)) {
            nextStartSlope = rightSlope;
            continue;
          } else {
            blocked = false;
            startSlope = nextStartSlope;
          }
        } else {
          if (isWallCallback(mapX, mapY) && i < this.viewRadius) {
            blocked = true;
            this.castLight(
              cx,
              cy,
              i + 1,
              startSlope,
              leftSlope,
              octant,
              isWallCallback
            );
            nextStartSlope = rightSlope;
          }
        }
      }

      if (blocked) break;
    }
  }

  /**
   * Transform coordinates based on octant
   */
  private transformOctant(
    cx: number,
    cy: number,
    dx: number,
    dy: number,
    octant: number
  ): [number, number] {
    switch (octant) {
      case 0: return [cx + dx, cy + dy];
      case 1: return [cx + dy, cy + dx];
      case 2: return [cx - dy, cy + dx];
      case 3: return [cx - dx, cy + dy];
      case 4: return [cx - dx, cy - dy];
      case 5: return [cx - dy, cy - dx];
      case 6: return [cx + dy, cy - dx];
      case 7: return [cx + dx, cy - dy];
      default: return [cx, cy];
    }
  }

  /**
   * Mark a tile as explored (permanently revealed on minimap)
   */
  private markExplored(x: number, y: number): void {
    const key = `${x},${y}`;
    let tile = this.tiles.get(key);
    if (!tile) {
      tile = { x, y, visible: true, explored: true };
      this.tiles.set(key, tile);
    }
    tile.explored = true;
    tile.visible = true;
  }

  /**
   * Check if a tile is currently visible
   */
  public isVisible(x: number, y: number): boolean {
    return this.visibleTiles.has(`${x},${y}`);
  }

  /**
   * Check if a tile has been explored (seen before)
   */
  public isExplored(x: number, y: number): boolean {
    const tile = this.tiles.get(`${x},${y}`);
    return tile?.explored ?? false;
  }

  /**
   * Reset all fog of war data
   */
  public reset(): void {
    this.tiles.clear();
    this.visibleTiles.clear();
  }

  /**
   * Get all visible tiles for rendering
   */
  public getVisibleTiles(): Set<string> {
    return this.visibleTiles;
  }

  /**
   * Set view radius
   */
  public setViewRadius(radius: number): void {
    this.viewRadius = radius;
  }
}
